import { useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  ListSubheader,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

/**
 * Calendario de partidos de fase de grupos (todos contra todos / manuales).
 * `groupMatchDialog` controla el diálogo de edición (estado en el padre).
 */
export default function TournamentSetupGroupMatchesTab({
  teams,
  groups,
  busy,
  sortedGroupMatches,
  scheduleGroupId,
  setScheduleGroupId,
  roundRobinDouble,
  setRoundRobinDouble,
  groupScheduleStart,
  setGroupScheduleStart,
  groupMatchSpacingMinutes,
  setGroupMatchSpacingMinutes,
  teamIdsInSelectedGroup,
  groupRoundRobinPreviewCount,
  manualLocalId,
  setManualLocalId,
  manualForeignId,
  setManualForeignId,
  manualDayHour,
  setManualDayHour,
  manualFieldName,
  setManualFieldName,
  handleAddManualGroupMatch,
  handleDeleteGroupMatch,
  handleGenerateGroupRoundRobin,
  onEditGroupMatch,
  groupMatchDialog,
}) {
  const gd = groupMatchDialog;

  // Mapa rápido id → equipo, y bucket por grupo (los equipos sin grupo van al final).
  const groupBuckets = useMemo(() => {
    const byId = new Map(teams.map((t) => [t.id, t]));
    const assigned = new Set();
    const buckets = [];
    for (const g of groups) {
      const memberships = g.tournamentGroupTeams || g.TournamentGroupTeams || [];
      const list = [];
      for (const gt of memberships) {
        const teamId = gt.teamId ?? gt.TeamId ?? gt.team?.id ?? gt.Team?.id;
        if (!teamId) continue;
        const team = byId.get(teamId);
        if (!team) continue;
        list.push(team);
        assigned.add(teamId);
      }
      if (list.length > 0) {
        buckets.push({ key: g.id, title: g.name || 'Grupo', list });
      }
    }
    const orphans = teams.filter((t) => !assigned.has(t.id));
    if (orphans.length > 0) {
      buckets.push({ key: '__no_group__', title: 'Sin grupo', list: orphans });
    }
    return buckets;
  }, [teams, groups]);

  // Devuelve el bucket (grupo) que contiene un equipo, o null si no está en ninguno.
  const groupOfTeam = (teamId) => {
    if (!teamId) return null;
    for (const bucket of groupBuckets) {
      if (bucket.list.some((t) => t.id === teamId)) return bucket;
    }
    return null;
  };

  /**
   * Equipos contra los que `localId` ya juega EN ESE SENTIDO (como local).
   * No incluye los partidos en sentido contrario, así se puede crear la vuelta:
   * si existe Eq1 vs Eq2, sigue siendo válido crear Eq2 vs Eq1.
   */
  const pairedAsLocalWith = (localId, excludeMatchId) => {
    if (!localId) return new Set();
    const set = new Set();
    for (const m of sortedGroupMatches) {
      if (excludeMatchId && m.id === excludeMatchId) continue;
      if (m.localTeamId === localId) set.add(m.foreignTeamId);
    }
    return set;
  };

  const isSameDirectionPaired = (localId, foreignId, excludeMatchId) => {
    if (!localId || !foreignId) return false;
    return pairedAsLocalWith(localId, excludeMatchId).has(foreignId);
  };

  /**
   * Devuelve los <MenuItem> agrupados por grupo (con <ListSubheader>),
   * con filtros opcionales: excluir un equipo, ocultar los que ya juegan
   * contra un local (en mismo sentido), y restringir a los equipos del mismo
   * grupo que cierto equipo.
   */
  const renderGroupedTeamItems = ({
    excludeTeamId,
    excludePairedWith,
    excludeMatchId,
    restrictToGroupOfTeamId,
  } = {}) => {
    const paired = excludePairedWith ? pairedAsLocalWith(excludePairedWith, excludeMatchId) : null;
    const restrictGroup = restrictToGroupOfTeamId ? groupOfTeam(restrictToGroupOfTeamId) : null;
    const bucketsToShow = restrictGroup ? [restrictGroup] : groupBuckets;
    return bucketsToShow.flatMap((bucket) => {
      const visible = bucket.list.filter((t) => {
        if (excludeTeamId && t.id === excludeTeamId) return false;
        if (paired && paired.has(t.id)) return false;
        return true;
      });
      if (visible.length === 0) return [];
      return [
        <ListSubheader
          key={`h-${bucket.key}`}
          sx={{
            lineHeight: '30px',
            fontWeight: 800,
            color: '#0f3531',
            bgcolor: '#f4f0e6',
            fontSize: '0.78rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          {bucket.title}
        </ListSubheader>,
        ...visible.map((t) => (
          <MenuItem key={t.id} value={t.id} sx={{ pl: 3.5 }}>
            {t.name}
          </MenuItem>
        )),
      ];
    });
  };

  // Campos/pistas usadas en partidos existentes (para sugerencias del autocompletar).
  const fieldNameOptions = useMemo(() => {
    const set = new Set();
    for (const m of sortedGroupMatches) {
      const f = (m.fieldName || '').trim();
      if (f) set.add(f);
    }
    return [...set].sort((a, b) => a.localeCompare(b, 'es', { numeric: true }));
  }, [sortedGroupMatches]);

  return (
    <>
      <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2 }}>
        <Typography variant="body2" component="span" sx={{ lineHeight: 1.65 }}>
          <strong>Manual:</strong> local, visitante, fecha/hora y campo opcional. La siguiente hora propuesta suma{' '}
          {groupMatchSpacingMinutes} min (configúralo abajo en automático). <strong>Automático:</strong> genera todos los
          cruces de un grupo (fase de grupos).
        </Typography>
      </Alert>

      {teams.length < 2 ? (
        <Alert severity="info">Hacen falta al menos 2 equipos en el torneo para programar partidos de grupo.</Alert>
      ) : (
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f3531', mb: 1.5 }}>
              Añadir partido manual
            </Typography>
            <Box
              component="form"
              onSubmit={handleAddManualGroupMatch}
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 2.5,
                border: '1px solid rgba(15,53,49,0.14)',
                bgcolor: '#fafcf9',
                boxShadow: '0 2px 12px rgba(15,53,49,0.06)',
              }}
            >
              <Stack spacing={2.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth size="medium">
                    <InputLabel id="manual-local-label" shrink>
                      Local
                    </InputLabel>
                    <Select
                      labelId="manual-local-label"
                      label="Local"
                      value={manualLocalId}
                      displayEmpty
                      renderValue={(v) =>
                        v ? teams.find((t) => t.id === v)?.name ?? v : 'Elige equipo local…'
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        setManualLocalId(v);
                        // Limpia el visitante si ya no es válido con el nuevo local:
                        // mismo equipo, distinto grupo, o pareja en el mismo sentido ya creada.
                        if (v && manualForeignId) {
                          const lg = groupOfTeam(v);
                          const fg = groupOfTeam(manualForeignId);
                          const sameGroup = (lg?.key || null) === (fg?.key || null);
                          if (
                            v === manualForeignId ||
                            !sameGroup ||
                            isSameDirectionPaired(v, manualForeignId)
                          ) {
                            setManualForeignId('');
                          }
                        }
                      }}
                    >
                      <MenuItem value="">
                        <em>Elige equipo local…</em>
                      </MenuItem>
                      {renderGroupedTeamItems()}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="medium">
                    <InputLabel id="manual-foreign-label" shrink>
                      Visitante
                    </InputLabel>
                    <Select
                      labelId="manual-foreign-label"
                      label="Visitante"
                      value={manualForeignId}
                      displayEmpty
                      renderValue={(v) =>
                        v ? teams.find((t) => t.id === v)?.name ?? v : 'Elige visitante…'
                      }
                      onChange={(e) => setManualForeignId(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>Elige visitante…</em>
                      </MenuItem>
                      {renderGroupedTeamItems({
                        excludeTeamId: manualLocalId,
                        excludePairedWith: manualLocalId,
                        restrictToGroupOfTeamId: manualLocalId,
                      })}
                    </Select>
                  </FormControl>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
                  El botón verde se activa cuando ambos equipos están elegidos.
                </Typography>
                <TextField
                  label="Fecha y hora del partido"
                  type="datetime-local"
                  size="medium"
                  value={manualDayHour}
                  onChange={(e) => setManualDayHour(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <Autocomplete
                  freeSolo
                  options={fieldNameOptions}
                  inputValue={manualFieldName}
                  onInputChange={(_, v) => setManualFieldName(v)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Campo / pista (opcional)"
                      placeholder="Ej. Campo 1"
                      size="medium"
                      fullWidth
                    />
                  )}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={busy || !manualLocalId || !manualForeignId}
                  fullWidth
                  sx={{
                    py: 1.35,
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: '1rem',
                    borderRadius: 2,
                    bgcolor: '#0f6b5c',
                    boxShadow: '0 4px 14px rgba(15,107,92,0.35)',
                    '&:hover': { bgcolor: '#0c5a4e' },
                    '&.Mui-disabled': {
                      bgcolor: 'rgba(15,107,92,0.38)',
                      color: 'rgba(255,255,255,0.92)',
                    },
                  }}
                >
                  Añadir partido de grupo
                </Button>
              </Stack>
            </Box>
          </Box>

          {sortedGroupMatches.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f3531', mb: 1.5 }}>
                Partidos de grupo ({sortedGroupMatches.length})
              </Typography>
              <Stack spacing={1.25}>
                {sortedGroupMatches.map((m) => {
                  const localName = m.localTeam?.name || m.localTeamId;
                  const foreignName = m.foreignTeam?.name || m.foreignTeamId;
                  const when = new Date(m.dayHour);
                  const whenStr = Number.isNaN(when.getTime())
                    ? String(m.dayHour)
                    : when.toLocaleString('es-ES', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      });
                  return (
                    <Paper
                      key={m.id}
                      variant="outlined"
                      sx={{
                        px: 2,
                        py: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        borderColor: 'rgba(15,53,49,0.12)',
                        bgcolor: '#fff',
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 700, color: '#0f3531' }}>
                        {localName}{' '}
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
                          vs
                        </Typography>{' '}
                        {foreignName}
                      </Typography>
                      <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap">
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                          {whenStr}
                          {m.fieldName ? ` · ${m.fieldName}` : ''}
                          {m.completed ? ' · Finalizado' : ''}
                        </Typography>
                        <IconButton
                          size="small"
                          aria-label="Editar partido de grupo"
                          onClick={() => onEditGroupMatch(m)}
                          disabled={busy}
                          sx={{ color: 'text.secondary' }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          aria-label="Eliminar partido"
                          onClick={() => handleDeleteGroupMatch(m.id)}
                          disabled={busy}
                          sx={{ color: 'text.secondary' }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          )}

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f3531', mb: 1 }}>
              Generación automática (opcional)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Crea todos los enfrentamientos del grupo (todos contra todos), con opción de doble vuelta y espaciado
              uniforme entre partidos.
            </Typography>
            {groups.length === 0 ? (
              <Alert severity="info">Crea al menos un grupo con equipos para usar esta ayuda.</Alert>
            ) : (
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grupo</InputLabel>
                  <Select
                    label="Grupo"
                    value={scheduleGroupId}
                    onChange={(e) => setScheduleGroupId(e.target.value)}
                  >
                    {groups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name} ({(g.tournamentGroupTeams || []).length} equipos)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox checked={roundRobinDouble} onChange={(e) => setRoundRobinDouble(e.target.checked)} />
                  }
                  label="Doble vuelta (ida y vuelta)"
                />
                <TextField
                  label="Primer partido (fecha y hora)"
                  type="datetime-local"
                  size="small"
                  value={groupScheduleStart}
                  onChange={(e) => setGroupScheduleStart(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
                <TextField
                  label="Minutos entre partidos"
                  type="number"
                  size="small"
                  value={groupMatchSpacingMinutes}
                  onChange={(e) => setGroupMatchSpacingMinutes(Number(e.target.value) || 90)}
                  inputProps={{ min: 15, step: 15 }}
                  fullWidth
                />
                <Typography variant="body2">
                  Vista previa: <strong>{groupRoundRobinPreviewCount}</strong> partidos
                  {teamIdsInSelectedGroup.length < 2 && ' (añade al menos 2 equipos al grupo)'}
                </Typography>
                <Button
                  variant="contained"
                  disabled={busy || groupRoundRobinPreviewCount === 0}
                  onClick={handleGenerateGroupRoundRobin}
                  className="!bg-btn-arena !font-bold"
                >
                  Generar partidos de grupo
                </Button>
              </Stack>
            )}
          </Box>
        </Stack>
      )}

      <Dialog open={gd.open} onClose={gd.onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800, color: '#0f3531' }}>Editar partido de grupo</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth size="medium">
              <InputLabel id="gm-local" shrink>
                Local
              </InputLabel>
              <Select
                labelId="gm-local"
                label="Local"
                value={gd.localId}
                displayEmpty
                renderValue={(v) => (v ? teams.find((t) => t.id === v)?.name ?? v : 'Elige…')}
                onChange={(e) => {
                  const v = e.target.value;
                  gd.setLocalId(v);
                  // Limpia el visitante si deja de ser válido con el nuevo local
                  // (mismo equipo, distinto grupo o pareja ya creada en este sentido).
                  if (v && gd.foreignId) {
                    const lg = groupOfTeam(v);
                    const fg = groupOfTeam(gd.foreignId);
                    const sameGroup = (lg?.key || null) === (fg?.key || null);
                    if (
                      v === gd.foreignId ||
                      !sameGroup ||
                      isSameDirectionPaired(v, gd.foreignId, gd.base?.id)
                    ) {
                      gd.setForeignId('');
                    }
                  }
                }}
              >
                {renderGroupedTeamItems()}
              </Select>
            </FormControl>
            <FormControl fullWidth size="medium">
              <InputLabel id="gm-foreign" shrink>
                Visitante
              </InputLabel>
              <Select
                labelId="gm-foreign"
                label="Visitante"
                value={gd.foreignId}
                displayEmpty
                renderValue={(v) => (v ? teams.find((t) => t.id === v)?.name ?? v : 'Elige…')}
                onChange={(e) => gd.setForeignId(e.target.value)}
              >
                {renderGroupedTeamItems({
                  excludeTeamId: gd.localId,
                  excludePairedWith: gd.localId,
                  excludeMatchId: gd.base?.id,
                  restrictToGroupOfTeamId: gd.localId,
                })}
              </Select>
            </FormControl>
            <TextField
              label="Fecha y hora"
              type="datetime-local"
              size="medium"
              value={gd.dayHour}
              onChange={(e) => gd.setDayHour(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Autocomplete
              freeSolo
              options={fieldNameOptions}
              inputValue={gd.fieldName}
              onInputChange={(_, v) => gd.setFieldName(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Campo / pista (opcional)"
                  size="medium"
                  fullWidth
                />
              )}
            />
            {gd.base?.completed ? (
              <Typography variant="caption" color="text.secondary">
                Partido finalizado: goles y ganador se ajustan desde la mesa; aquí puedes corregir hora, campo o equipos
                si aún no se ha jugado (cuidado con la clasificación).
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={gd.onClose} disabled={gd.busy} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={gd.onSave}
            disabled={gd.busy || !gd.localId || !gd.foreignId}
            sx={{ textTransform: 'none', fontWeight: 800, bgcolor: '#0f6b5c', '&:hover': { bgcolor: '#0c5a4e' } }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
